import uniqBy from 'lodash.uniqby';
import flatten from 'lodash.flatten';

import { ALBUM_SUBTYPE } from '../../../../../../../constants/albums';
import { getAlbumMembershipGroupEntity } from '../../../../../../temp/views/memberships/AlbumMembershipGroup/helpers';
import { isAlbumFolder } from '../../../../../../../util/album-folder';
import { DIGITAL_DOWNLOADS_MODE } from '../../../../../../../constants/print-packages';
import { PRINT_ASSET_TYPES } from '../../../../../../../constants/print-assets';

/**
 * Returns an AlbumMembershipGroup's photo pricing.
 *
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup.
 * @param {Boolean} sellPrintsEnabled If print sales are enabled
 * @returns {undefined|Object}
 */
export const getAlbumMembershipGroupPhotoPricing = (
  albumMembershipGroup,
  sellPrintsEnabled = true,
) => {
  if (!albumMembershipGroup || !sellPrintsEnabled) {
    return undefined;
  }

  return albumMembershipGroup?.photo_pricing;
};

/**
 * Returns the AlbumPrintPackages of an AlbumMembershipGroup.
 *
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup.
 * @param {Boolean} excludeDownloadIncludedPackages whether to exclude or not packages with download included
 * @param {Array} jerseyColors the jersey colors used by the active identity
 * @param {Boolean} sellPrintsEnabled whether the account is international or not
 * @returns {Array}
 */
export const getAlbumMembershipGroupAlbumPrintPackages = (
  albumMembershipGroup,
  excludeDownloadIncludedPackages = false,
  jerseyColors = [],
  sellPrintsEnabled = true,
) => {
  if (!albumMembershipGroup || !sellPrintsEnabled) {
    return [];
  }

  // We will respect albumMembershipGroup print packages so long as we have at least 1.
  // If we don't, but have printPackages on the album, we'll fallback to those.
  const printPackages =
    albumMembershipGroup.print_packages?.length === 0 &&
    albumMembershipGroup.album?.root_album_folder?.album_folder_print_packages
      ?.total > 0
      ? albumMembershipGroup.album.root_album_folder.album_folder_print_packages
          .records
      : albumMembershipGroup.print_packages;

  if (excludeDownloadIncludedPackages) {
    const filteredPackages = (printPackages ?? []).filter(
      albumPrintPackage =>
        albumPrintPackage.print_package?.digital_downloads_mode ===
        DIGITAL_DOWNLOADS_MODE.NOT_INCLUDED,
    );

    if (jerseyColors.length > 0) {
      return filteredPackages.filter(
        ({ print_package: printPackage }) =>
          !printPackage?.jersey_colors?.records?.length ||
          !!printPackage?.jersey_colors?.records?.find(
            ({ jersey_color: color }) => jerseyColors.includes(color),
          ),
      );
    }

    return filteredPackages;
  }

  if (jerseyColors.length > 0) {
    return printPackages.filter(
      ({ print_package: printPackage }) =>
        !printPackage?.jersey_colors?.records?.length ||
        !!printPackage?.jersey_colors?.records?.find(
          ({ jersey_color: color }) => jerseyColors.includes(color),
        ),
    );
  }

  return printPackages ?? [];
};

/**
 * Returns an AlbumMembershipGroup's photo pricing.
 *
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup.
 * @returns {undefined|Object}
 */
export const getAlbumMembershipGroupWebGalleryPrice = albumMembershipGroup => {
  if (!albumMembershipGroup) {
    return undefined;
  }

  const entity = getAlbumMembershipGroupEntity(albumMembershipGroup);

  if (!entity) {
    return undefined;
  }

  const albumFolder = isAlbumFolder(entity) ? entity : entity.root_album_folder;

  if (
    !albumFolder?.web_gallery_price_per_photo?.value ||
    !albumFolder?.is_web_gallery
  ) {
    return undefined;
  }

  return albumFolder.web_gallery_price_per_photo;
};

/**
 * Returns the print items of an AlbumMembershipGroup for individual prints purchase.
 *
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup.
 * @returns {Array}
 */
export const getAlbumMembershipGroupPrintItems = albumMembershipGroup => {
  if (!albumMembershipGroup) {
    return [];
  }

  return albumMembershipGroup?.photo_pricing?.print_items?.records ?? [];
};

/**
 * Returns whether an AlbumMembershipGroup has individual photo pricing (PPD).
 *
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup.
 * @returns {boolean}
 */
export const albumMembershipGroupHasPhotoPurchasing = albumMembershipGroup => {
  if (!albumMembershipGroup) {
    return false;
  }

  const hasPhotoPricing =
    !!getAlbumMembershipGroupPhotoPricing(albumMembershipGroup);
  const hasPrintPackages =
    getAlbumMembershipGroupAlbumPrintPackages(albumMembershipGroup).length > 0;

  return hasPhotoPricing || hasPrintPackages;
};

/**
 * Returns whether an AlbumMembershipGroup has a Web Gallery Pricing value
 *
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup.
 * @returns {boolean}
 */
export const albumMembershipGroupHasWebGalleryPrice = albumMembershipGroup => {
  if (!albumMembershipGroup) {
    return false;
  }

  const entity = getAlbumMembershipGroupEntity(albumMembershipGroup);
  const albumFolder = isAlbumFolder(entity) ? entity : entity.root_album_folder;

  return (
    albumFolder?.is_web_gallery && !!albumFolder?.web_gallery_price_per_photo
  );
};

/**
 * Returns whether an AlbumFolder is "Share Photos" type.
 *
 * @param {Object} albumFolder The AlbumFolder.
 * @returns {boolean}
 */
const isAlbumFolderSharePhotos = albumFolder => {
  if (!albumFolder?.album_delivery_modes) {
    return false;
  }

  return (
    albumFolder.album_delivery_modes.photo_finder ||
    albumFolder.album_delivery_modes.shutterbug
  );
};

/**
 * Returns whether an Album or AlbumFolder (via its AlbumMembershipGroup)
 * should enable legacy print purchasing (print photo items).
 *
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup.
 * @param {Boolean} sellPrintsEnabled If print sales are enabled
 * @returns {boolean}
 */
export const albumMembershipGroupSupportsLegacyPrintPurchasing = (
  albumMembershipGroup,
  sellPrintsEnabled = true,
) => {
  if (!sellPrintsEnabled) {
    return false;
  }

  const entity =
    albumMembershipGroup?.album ?? albumMembershipGroup?.album_folder;
  const isCampContent = entity?.album_subtype === ALBUM_SUBTYPE.CAMPS;

  // Enable only on "Share Photos" type of content and Camps
  return (
    isAlbumFolderSharePhotos(entity) ||
    entity?.search_subscription_credits_joins_enabled ||
    entity?.web_gallery_print_purchases_enabled ||
    entity?.pre_paid ||
    isCampContent
  );
};

/**
 * Returns the price of a digital download (individual photo).
 *
 * @param {Object} photo The photo.
 * @param {Object} [photo.price_album_folder_tag] The photo's pricing tag.
 * @param {Object} photoPricing The album's photo pricing.
 * @returns {null|{value: number,currency: CURRENCY}}
 */
export const getDigitalDownloadPhotoPrice = (photo, photoPricing) => {
  if (!photoPricing) {
    return null;
  }

  if (!photo) {
    return photoPricing.digital_downloads_price;
  }

  if (photo.price_album_folder_tag?.digital_downloads_price?.value) {
    return photo.price_album_folder_tag.digital_downloads_price;
  }

  return photoPricing.digital_downloads_price;
};

/**
 * Returns the unique print backgrounds associated with any package or pricing template on the albumMembershipGroup
 *
 * @param {Object} albumMembershipGroup the albumMembershipGroup
 * @param {Array} jerseyColors the jersey colors used by the active identity
 * @returns {Array|*[]}
 */
export const getAlbumMembershipGroupPrintBackgrounds = (
  albumMembershipGroup,
  jerseyColors = [],
) => {
  if (!albumMembershipGroup) {
    return [];
  }

  const entity = getAlbumMembershipGroupEntity(albumMembershipGroup);
  const albumFolder = isAlbumFolder(entity)
    ? entity
    : entity?.root_album_folder;

  if (albumFolder?.print_assets?.records?.length > 0) {
    const assets = (albumFolder?.print_assets?.records ?? [])
      .filter(
        ({ print_asset: printAsset }) =>
          printAsset.type === PRINT_ASSET_TYPES.BACKGROUND,
      )
      .map(albumFolderPrintAsset => albumFolderPrintAsset.print_asset);

    if (jerseyColors.length > 0) {
      return assets.filter(
        ({ jersey_colors: assetColors }) =>
          !assetColors?.records?.length ||
          !!assetColors?.records?.find(({ jersey_color: color }) =>
            jerseyColors.includes(color),
          ),
      );
    }

    return assets;
  }

  const albumPrintPackages =
    getAlbumMembershipGroupAlbumPrintPackages(albumMembershipGroup);
  const photoPricing =
    getAlbumMembershipGroupPhotoPricing(albumMembershipGroup);

  const packageBackgrounds = flatten(
    albumPrintPackages.map(albumPrintPackage => {
      const printPackage = albumPrintPackage?.print_package;

      if (!printPackage) {
        return [];
      }

      return (printPackage?.print_assets?.records ?? [])
        .filter(
          ({ print_asset: printAsset }) =>
            printAsset.type === PRINT_ASSET_TYPES.BACKGROUND,
        )
        .map(printPackagePrintAsset => printPackagePrintAsset.print_asset);
    }),
  );

  const photoPricingBackgrounds = photoPricing
    ? (photoPricing?.print_assets?.records ?? [])
        .filter(
          ({ print_asset: printAsset }) =>
            printAsset.type === PRINT_ASSET_TYPES.BACKGROUND,
        )
        .map(printPackagePrintAsset => printPackagePrintAsset.print_asset)
    : [];

  const assets = uniqBy(
    [...packageBackgrounds, ...photoPricingBackgrounds],
    asset => asset.id,
  );

  if (jerseyColors.length > 0) {
    return assets.filter(
      ({ jersey_colors: assetColors }) =>
        !assetColors?.records?.length ||
        !!assetColors?.records?.find(({ jersey_color: color }) =>
          jerseyColors.includes(color),
        ),
    );
  }

  return assets;
};

export const getAlbumMembershipGroupHasJerseyColors = albumMembershipGroup => {
  const printBackgrounds =
    getAlbumMembershipGroupPrintBackgrounds(albumMembershipGroup);
  const albumCustomPrints =
    albumMembershipGroup?.account_card_album_custom_prints?.records ?? [];

  return (
    printBackgrounds.some(
      background => background.jersey_colors?.records?.length > 0,
    ) ||
    albumCustomPrints.some(
      ({ custom_print }) => custom_print.jersey_colors?.records?.length > 0,
    )
  );
};

export const createAlbumMembershipGroupEntity = (
  albumMembershipGroup,
  album,
) => {
  if (!albumMembershipGroup && !album) {
    return null;
  }

  return (
    albumMembershipGroup || {
      id: album.id,
      album,
      is_album: true,
      is_photo_entitled: album?.isPhotoEntitled,
      album_custom_prints: album?.root_album_folder?.album_custom_prints,
      collage_custom_prints: album?.root_album_folder?.collage_custom_prints,
      account_card_album_custom_prints:
        album?.root_album_folder?.account_card_album_custom_prints,
      specialty_custom_prints:
        album?.root_album_folder?.specialty_custom_prints,
      photo_pricing: album?.root_album_folder?.photo_pricing,
      print_packages:
        album?.root_album_folder?.album_folder_print_packages?.records ??
        album?.album_print_packages?.records,
    }
  );
};
